import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import TalkingAvatar from '../components/TalkingAvatar';
import {
  ArrowLeft, Bot, Send, Loader, Plus, MessageSquare, Trash2,
  Wrench, Brain, ChevronRight, Zap, Clock, X, Settings,
  Mic, MicOff, Volume2, VolumeX, User,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/compass/agent`;

const TOOL_LABELS = {
  cashflow_analyse: { icon: '📊', label: 'Cashflow Analyse' },
  beslissing_simuleren: { icon: '🎯', label: 'Beslissing Simuleren' },
  cv_analyseren: { icon: '📄', label: 'CV Analyseren' },
  kandidaten_vergelijken: { icon: '👥', label: 'Kandidaten Vergelijken' },
  geheugen_opslaan: { icon: '💾', label: 'Geheugen Opslaan' },
  geheugen_ophalen: { icon: '🔍', label: 'Geheugen Ophalen' },
};

// Strip markdown for TTS
const stripMarkdown = (md) =>
  ((md) == null ? '' : String(md)).replace(/#{1,6}\s?/g, '')
    .replace(/\*\*|__/g, '')
    .replace(/\*|_/g, '')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*+]\s/gm, '')
    .replace(/^\s*\d+\.\s/gm, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .trim();

// SpeechRecognition browser API
const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const AgentAssistant = () => {
  const { token, user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [memories, setMemories] = useState([]);
  const [showMemory, setShowMemory] = useState(false);

  // Voice states
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showAvatar, setShowAvatar] = useState(true);
  const recognitionRef = useRef(null);
  const avatarRef = useRef(null);
  const ttsAbortRef = useRef(false);
  const ttsTimerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const txt = {
    nl: {
      title: 'AI Assistent',
      subtitle: 'Jouw autonome bedrijfsassistent',
      placeholder: 'Vraag me iets... of klik op de microfoon',
      newChat: 'Nieuw Gesprek',
      sessions: 'Gesprekken',
      noSessions: 'Nog geen gesprekken — begin er een om te starten.',
      toolUsed: 'Tool gebruikt',
      thinking: 'Denken & uitvoeren...',
      memory: 'Geheugen',
      noMemory: 'Nog geen herinneringen opgeslagen — wat je bewaart verschijnt hier.',
      deleteMemory: 'Verwijder',
      welcome: 'Hallo! Ik ben Emma, je AI Assistent',
      welcomeDesc: 'Ik kan je helpen met complexe taken door automatisch de juiste Krypton9 tools te gebruiken. Beschrijf je uitdaging en ik wijs je de weg!',
      suggestions: [
        'Analyseer onze cashflow situatie: omzet 50K/maand, kosten 42K',
        'Moet ik een extra medewerker aannemen? We groeien 20% per jaar',
        'Onthoud: ons bedrijf heeft 12 medewerkers en zit in de IT-sector',
        'Wat weet je nog over ons bedrijf?',
      ],
      listening: 'Luisteren...',
      voiceOn: 'Stem aan',
      voiceOff: 'Stem uit',
      noVoice: 'Spraakherkenning wordt niet ondersteund in deze browser',
    },
    en: {
      title: 'AI Assistant',
      subtitle: 'Your autonomous business assistant',
      placeholder: 'Ask me anything... or click the microphone',
      newChat: 'New Chat',
      sessions: 'Conversations',
      noSessions: 'No conversations yet — start one to begin.',
      toolUsed: 'Tool used',
      thinking: 'Thinking & executing...',
      memory: 'Memory',
      noMemory: 'No memories stored yet — anything you save will appear here.',
      deleteMemory: 'Delete',
      welcome: 'Hello! I\'m Emma, your AI Assistant',
      welcomeDesc: 'I can help with complex tasks by automatically using the right Krypton9 tools. Describe your challenge and I\'ll guide you!',
      suggestions: [
        'Analyze our cashflow: revenue 50K/month, costs 42K',
        'Should I hire an extra employee? We grow 20% per year',
        'Remember: our company has 12 employees in the IT sector',
        'What do you remember about our company?',
      ],
      listening: 'Listening...',
      voiceOn: 'Voice on',
      voiceOff: 'Voice off',
      noVoice: 'Speech recognition is not supported in this browser',
    }
  }[lang] || {
    title: 'AI Assistant', subtitle: '', placeholder: 'Ask...', newChat: 'New',
    sessions: 'Sessions', noSessions: 'None', toolUsed: 'Tool', thinking: 'Thinking...',
    memory: 'Memory', noMemory: 'No memory', deleteMemory: 'Delete',
    welcome: 'Hello!', welcomeDesc: '', suggestions: [], listening: 'Listening...',
    voiceOn: 'Voice on', voiceOff: 'Voice off', noVoice: 'Not supported',
  };

  useEffect(() => { fetchSessions(); fetchMemories(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      ttsAbortRef.current = true;
      if (ttsTimerRef.current) clearTimeout(ttsTimerRef.current);
      if (recognitionRef.current) recognitionRef.current.abort();
      window.speechSynthesis?.cancel();
      if (window._hyperTTSAudio) {
        window._hyperTTSAudio.pause();
        window._hyperTTSAudio = null;
      }
      avatarRef.current?.stopSpeaking?.();
    };
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/sessions`, { headers });
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const fetchMemories = async () => {
    try {
      const res = await axios.get(`${API}/memory`, { headers });
      setMemories(res.data.memories || []);
    } catch (err) {
      console.error('Error fetching memories:', err);
    }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/session/${sid}`, { headers });
      setMessages(res.data.messages || []);
      setSessionId(sid);
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const deleteSession = async (sid) => {
    try {
      await axios.delete(`${API}/session/${sid}`, { headers });
      setSessions(prev => (Array.isArray(prev) ? prev : []).filter(s => s.session_id !== sid));
      if (sessionId === sid) startNew();
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const deleteMemoryEntry = async (key) => {
    try {
      await axios.delete(`${API}/memory/${encodeURIComponent(key)}`, { headers });
      setMemories(prev => (Array.isArray(prev) ? prev : []).filter(m => m.key !== key));
    } catch (err) {
      console.error('Error deleting memory:', err);
    }
  };

  // ─── TTS: Speak text aloud - sentence-by-sentence for instant start ───
  const speakText = useCallback(async (text) => {
    if (!voiceEnabled) return;

    const clean = stripMarkdown(text);
    if (!clean) return;

    // Split into sentences for fast first response
    const sentences = clean.match(/[^.!?;:]+[.!?;:]?/g) || [clean];
    const chunks = [];
    let current = '';
    for (const s of sentences) {
      current += s;
      if (current.length > 60) { // group into ~60 char chunks
        chunks.push(((current) == null ? '' : String(current)).trim());
        current = '';
      }
    }
    if (((current) == null ? '' : String(current)).trim()) chunks.push(((current) == null ? '' : String(current)).trim());
    if (chunks.length === 0) return;

    setIsSpeaking(true);
    ttsAbortRef.current = false;

    // Play chunks sequentially
    const playChunk = async (idx) => {
      if (ttsAbortRef.current || idx >= chunks.length) {
        setIsSpeaking(false);
        return;
      }
      try {
        const avatar = avatarRef.current;
        const useSync = avatar?.isReady?.();

        if (useSync) {
          // Use sync endpoint for precise word-level lip-sync timing
          const res = await fetch(`${BACKEND_URL}/api/tts/speak-sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: chunks[idx], lang }),
          });
          if (!res.ok) throw new Error();
          const data = await res.json();

          // Decode base64 audio to blob
          const raw = atob(data.audio);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
          const blob = new Blob([bytes], { type: 'audio/mpeg' });

          const played = await avatar.speakAudio(blob, {
            words: data.words,
            wtimes: data.wtimes,
            wdurations: data.wdurations,
          });
          if (played) {
            // Use actual last word end time for accurate sequencing
            const lastEnd = data.wtimes.length > 0
              ? data.wtimes[data.wtimes.length - 1] + data.wdurations[data.wdurations.length - 1] + 400
              : Math.max(1500, chunks[idx].length * 65);
            ttsTimerRef.current = setTimeout(() => playChunk(idx + 1), lastEnd);
            return;
          }
        }

        // Fallback: plain audio without lip-sync
        const res = await fetch(`${BACKEND_URL}/api/tts/speak`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: chunks[idx], lang }),
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { URL.revokeObjectURL(url); if (!ttsAbortRef.current) playChunk(idx + 1); };
        audio.onerror = () => { URL.revokeObjectURL(url); if (!ttsAbortRef.current) playChunk(idx + 1); };
        audio.play();
        window._hyperTTSAudio = audio;
      } catch {
        if (!ttsAbortRef.current) playChunk(idx + 1);
      }
    };

    // Pre-fetch chunk 2 while chunk 1 plays (use sync if avatar ready)
    if (chunks.length > 1) {
      const endpoint = avatarRef.current?.isReady?.() ? '/api/tts/speak-sync' : '/api/tts/speak';
      fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: chunks[1], lang }),
      }).catch(() => {});
    }

    playChunk(0);
  }, [voiceEnabled, lang]);

  // ─── STT: Start listening ───
  const startListening = () => {
    if (!SpeechRecognition) {
      alert(txt.noVoice);
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'nl' ? 'nl-NL' : 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const results = Array.from(event.results);
      const transcript = (Array.isArray(results) ? results : []).map(r => r[0].transcript).join('');
      setInput(transcript);

      // Auto-send when final result
      if ((Array.isArray(results) ? results : []).some(r => r.isFinal)) {
        setIsListening(false);
        if (((transcript) == null ? '' : String(transcript)).trim()) {
          setTimeout(() => sendMessageDirect(((transcript) == null ? '' : String(transcript)).trim()), 200);
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  };

  // Direct send (bypasses input state timing)
  const sendMessageDirect = async (text) => {
    if (!text?.trim() || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
        language: lang,
      }, { headers });

      const { session_id, response, tools_used, actions } = res.data;
      setSessionId(session_id);

      const assistantMsg = {
        role: 'assistant',
        content: response,
        tools_used: tools_used || [],
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Execute navigation actions — Emma opens tools directly
      if (actions?.length > 0) {
        for (const action of actions) {
          if (action.type === 'navigate' && action.route) {
            setTimeout(() => navigate(action.route), 800);
          }
        }
      }

      // Speak the response
      speakText(response);

      fetchSessions();
      if (tools_used?.some(t => t.tool === 'geheugen_opslaan')) {
        fetchMemories();
      }
    } catch (err) {
      const errMsg = `Error: ${err.response?.data?.detail || 'Request failed'}`;
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const sendMessage = async (msg) => {
    const text = msg || input;
    await sendMessageDirect(text);
  };

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  };

  const stopSpeaking = () => {
    ttsAbortRef.current = true;
    if (ttsTimerRef.current) { clearTimeout(ttsTimerRef.current); ttsTimerRef.current = null; }
    window.speechSynthesis?.cancel();
    if (window._hyperTTSAudio) {
      window._hyperTTSAudio.pause();
      window._hyperTTSAudio.currentTime = 0;
      window._hyperTTSAudio = null;
    }
    avatarRef.current?.stopSpeaking?.();
    setIsSpeaking(false);
  };

  return (
    <div className="compass-coach-page" data-testid="agent-page">
      {/* Sidebar */}
      <aside className="compass-sidebar" style={{ background: 'var(--bg-card)' }}>
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
          <ArrowLeft size={14} /> AI Tools
        </button>

        <div className="compass-role-badge" style={{ background: 'var(--accent-blue-200)' }}>
          <Bot size={16} />
          <span className="body-small" style={{ fontWeight: 600 }}>{txt.title}</span>
        </div>

        <button className="compass-new-chat" onClick={startNew} data-testid="new-chat-btn">
          <Plus size={14} /> {txt.newChat}
        </button>

        {/* Voice toggle */}
        <button
          className="compass-nav-item"
          onClick={() => { setVoiceEnabled(!voiceEnabled); if (isSpeaking) stopSpeaking(); }}
          style={{ justifyContent: 'space-between' }}
          data-testid="voice-toggle"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {voiceEnabled ? txt.voiceOn : txt.voiceOff}
          </span>
        </button>

        {/* Avatar toggle */}
        <button
          className="compass-nav-item"
          onClick={() => setShowAvatar(!showAvatar)}
          style={{ justifyContent: 'space-between' }}
          data-testid="avatar-toggle"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <User size={14} />
            {showAvatar ? (lang === 'nl' ? 'Avatar aan' : 'Avatar on') : (lang === 'nl' ? 'Avatar uit' : 'Avatar off')}
          </span>
        </button>

        {/* Memory toggle */}
        <button className="compass-nav-item" onClick={() => setShowMemory(!showMemory)}
          style={{ justifyContent: 'space-between' }} data-testid="memory-toggle">
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Brain size={14} /> {txt.memory} ({memories.length})
          </span>
          <ChevronRight size={14} style={{ transform: showMemory ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
        </button>

        {showMemory && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
            {memories.length === 0 && (
              <span className="caption text-secondary" style={{ fontStyle: 'italic' }}>{txt.noMemory}</span>
            )}
            {(Array.isArray(memories) ? memories : []).map(m => (
              <div key={m.key} className="compass-nav-item" style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem', justifyContent: 'space-between' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.key}
                </span>
                <span onClick={(e) => { e.stopPropagation(); deleteMemoryEntry(m.key); }}
                  style={{ cursor: 'pointer', opacity: 0.4, flexShrink: 0 }}>
                  <X size={12} />
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Sessions */}
        <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-light)', paddingTop: '0.5rem' }}>
          <span className="caption text-secondary" style={{ display: 'block', marginBottom: '0.5rem' }}>
            <Clock size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {txt.sessions}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', overflowY: 'auto', maxHeight: '300px' }}>
            {sessions.length === 0 && (
              <span className="caption text-secondary" style={{ fontStyle: 'italic' }}>{txt.noSessions}</span>
            )}
            {(Array.isArray(sessions) ? sessions : []).map(s => (
              <div key={s.session_id}
                className={`compass-nav-item ${sessionId === s.session_id ? 'active' : ''}`}
                style={{ padding: '0.4rem 0.5rem', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => loadSession(s.session_id)} data-testid={`session-${s.session_id}`}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                  {s.preview || 'Gesprek'}
                </span>
                <span onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                  style={{ cursor: 'pointer', opacity: 0.4, flexShrink: 0 }}>
                  <Trash2 size={12} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Main Chat */}
      <main className="compass-main">
        <div className="compass-chat-container">
          <div className="compass-chat-messages" data-testid="agent-messages">
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: '600px', margin: '0 auto' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '1rem', margin: '0 auto 1rem',
                  background: 'var(--accent-blue-200)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Bot size={28} style={{ color: 'var(--accent-blue)' }} />
                </div>
                <h2 className="heading-2" style={{ marginBottom: '0.5rem' }}>{txt.welcome}</h2>
                <p className="body-small text-secondary" style={{ marginBottom: '1.5rem' }}>{txt.welcomeDesc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(Array.isArray(txt.suggestions) ? txt.suggestions : []).map((s, i) => (
                    <button key={i} className="compass-suggestion-btn"
                      style={{ textAlign: 'left', padding: '0.75rem 1rem' }}
                      onClick={() => sendMessage(s)} data-testid={`suggestion-${i}`}>
                      <Zap size={14} style={{ flexShrink: 0 }} /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <div>
                    {msg.tools_used && msg.tools_used.length > 0 && (
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.75rem'
                      }}>
                        {(Array.isArray(msg.tools_used) ? msg.tools_used : []).map((t, ti) => {
                          const toolInfo = TOOL_LABELS[t.tool] || { icon: '🔧', label: t.tool };
                          return (
                            <span key={ti} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem',
                              background: 'var(--accent-blue-200)', color: 'var(--accent-blue)',
                              fontWeight: 500,
                            }} data-testid={`tool-badge-${t.tool}`}>
                              <Wrench size={10} /> {toolInfo.label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="compass-msg-content markdown-content">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    {/* Speak this message button */}
                    {voiceEnabled && (
                      <button
                        onClick={() => speakText(msg.content)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          opacity: 0.4, marginTop: 4, padding: '2px 4px',
                        }}
                        title={lang === 'nl' ? 'Voorlezen' : 'Read aloud'}
                        data-testid={`speak-msg-${i}`}
                      >
                        <Volume2 size={14} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="compass-msg-content">{msg.content}</div>
                )}
              </div>
            ))}

            {loading && (
              <div className="compass-msg compass-msg-assistant">
                <div className="compass-msg-content" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Loader className="spin" size={16} />
                  <span>{txt.thinking}</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input bar */}
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
            {/* Listening indicator */}
            {isListening && (
              <div style={{
                position: 'absolute', top: -36, left: '50%', transform: 'translateX(-50%)',
                background: '#ef4444', color: '#fff', padding: '4px 16px', borderRadius: '1rem',
                fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                animation: 'pulse 1.5s infinite',
              }} data-testid="listening-indicator">
                <Mic size={14} /> {txt.listening}
              </div>
            )}

            {/* Speaking indicator */}
            {isSpeaking && (
              <button
                type="button"
                onClick={stopSpeaking}
                style={{
                  position: 'absolute', top: -36, left: '50%', transform: 'translateX(-50%)',
                  background: '#6366f1', color: '#fff', padding: '4px 16px', borderRadius: '1rem',
                  fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  border: 'none', cursor: 'pointer',
                }}
                data-testid="speaking-indicator"
              >
                <Volume2 size={14} /> {lang === 'nl' ? 'Spreken... (klik om te stoppen)' : 'Speaking... (click to stop)'}
              </button>
            )}

            {/* Mic button */}
            <button
              type="button"
              onClick={startListening}
              disabled={loading}
              className={`voice-mic-btn ${isListening ? 'listening' : ''}`}
              data-testid="voice-mic-btn"
              title={isListening ? (lang === 'nl' ? 'Stop luisteren' : 'Stop listening') : (lang === 'nl' ? 'Spreek je vraag in' : 'Speak your question')}
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={txt.placeholder}
              disabled={loading}
              className="form-input"
              data-testid="agent-input"
            />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"
              data-testid="agent-send-btn">
              <Send size={16} />
            </button>
          </form>
        </div>
      </main>

      {/* Avatar Panel - Right side */}
      {showAvatar && (
        <aside className="agent-avatar-panel" data-testid="avatar-panel">
          <TalkingAvatar ref={avatarRef} isSpeaking={isSpeaking} lang={lang} visible={showAvatar} />
        </aside>
      )}
    </div>
  );
};

export default AgentAssistant;
